import { ComponentMetadataModel, ParameterMetadata, ParameterType, PrimitiveType } from '../../../api/api';
import { ParameterTypes } from '../../../common/ParameterTypes';
import { IComponentModel } from '../Models/IComponentModel';
import { ComponentPath } from './ComponentTreeUtils';

export type VirtualCollectionSelection = {
	componentPath: ComponentPath;
	parameter: ParameterMetadata;
	itemIndex: number;
	virtualMetadata: ComponentMetadataModel;
};

export type ResolvedVirtualCollectionSelection = VirtualCollectionSelection & {
	component: IComponentModel;
};

export const VIRTUAL_COLLECTION_ITEM_MODEL_TYPE_PROPERTY = '__VirtualCollectionItemModelType';

export function getPotentialCollectionItemSelection(
	path: ComponentPath | null,
): { componentPath: ComponentPath; parameterPathSegment: string; itemIndex: number } | null {
	if (!path) {
		return null;
	}

	if (path.length < 3) {
		return null;
	}

	const itemIndexSegment = path[path.length - 1];
	if (!/^\d+$/.test(itemIndexSegment)) {
		return null;
	}

	const itemIndex = Number.parseInt(itemIndexSegment, 10);
	if (!Number.isInteger(itemIndex) || itemIndex < 0) {
		return null;
	}

	return {
		componentPath: path.slice(0, -2),
		parameterPathSegment: path[path.length - 2],
		itemIndex,
	};
}

export function getComponentTreePath(path: ComponentPath | null): ComponentPath | null {
	const collectionItemSelection = getPotentialCollectionItemSelection(path);
	return collectionItemSelection ? collectionItemSelection.componentPath : path;
}

export function toPathSegment(parameterName: string): string {
	return parameterName.length === 0 ? parameterName : parameterName[0].toLowerCase() + parameterName.slice(1);
}

export function matchesParameterPathSegment(parameter: ParameterMetadata, pathSegment: string): boolean {
	return parameter.name === pathSegment || toPathSegment(parameter.name) === pathSegment;
}

function createListWithSideDetailItemsParameter(): ParameterMetadata {
	return {
		name: 'Items',
		displayName: 'Items',
		order: 4,
		type: ParameterType.Custom,
		typeCustom: 'ListWithSideDetailItemModel',
		categories: [],
		isCollection: true,
		expectedType: { type: PrimitiveType.Object },
	};
}

function createListItemsParameter(): ParameterMetadata {
	return {
		name: 'Items',
		displayName: 'Items',
		order: 12,
		type: ParameterType.Custom,
		typeCustom: 'ListItemModel',
		categories: [],
		isCollection: true,
		expectedType: { type: PrimitiveType.Object },
	};
}

function createListWithSideDetailHeaderActionsParameter(): ParameterMetadata {
	return {
		name: 'HeaderActions',
		displayName: 'Header Actions',
		order: 4,
		type: ParameterType.Custom,
		typeCustom: 'TableColumnAction',
		categories: [],
		isCollection: true,
		expectedType: { type: PrimitiveType.Object },
	};
}

function createListMessageChatMessageParameter(): ParameterMetadata {
	return {
		name: 'Message',
		displayName: 'Message',
		order: 11,
		type: ParameterType.Custom,
		typeCustom: 'ListMessageChatItemModel',
		categories: [],
		isCollection: true,
		expectedType: { type: PrimitiveType.Object },
	};
}

function createHeaderActionsParameter(): ParameterMetadata {
	return {
		name: 'HeaderActions',
		displayName: 'Header Actions',
		order: 3,
		type: ParameterType.Custom,
		typeCustom: 'TableColumnAction',
		categories: [],
		isCollection: true,
		expectedType: { type: PrimitiveType.Object },
	};
}

function createItemActionsParameter(): ParameterMetadata {
	return {
		name: 'ItemActions',
		displayName: 'Item Actions',
		order: 22,
		type: ParameterType.Custom,
		typeCustom: 'TableColumnAction',
		categories: [],
		isCollection: true,
		expectedType: { type: PrimitiveType.Object },
	};
}

function createTableColumnActionParameterFromMetadata(parameter: ParameterMetadata): ParameterMetadata {
	return {
		...parameter,
		type: ParameterType.Custom,
		typeCustom: 'TableColumnAction',
		isCollection: true,
		expectedType: { type: PrimitiveType.Object },
	};
}

function normalizeVirtualCollectionParameterFromMetadata(parameter: ParameterMetadata | undefined): ParameterMetadata | undefined {
	if (!parameter) {
		return undefined;
	}

	if (parameter.typeCustom === ParameterTypes.Actions) {
		return createTableColumnActionParameterFromMetadata(parameter);
	}

	return parameter;
}

const legacyVirtualCollectionParameters: Record<string, Record<string, () => ParameterMetadata>> = {
	List: {
		items: createListItemsParameter,
		headerActions: createHeaderActionsParameter,
		itemActions: createItemActionsParameter,
	},
	ListMessageChat: {
		message: createListMessageChatMessageParameter,
		headerActions: createHeaderActionsParameter,
	},
	ListWithSideDetail: {
		items: createListWithSideDetailItemsParameter,
		headerActions: createListWithSideDetailHeaderActionsParameter,
	},
};

function getLegacyFallbackParameter(componentType: string, pathSegment: string): ParameterMetadata | undefined {
	return legacyVirtualCollectionParameters[componentType]?.[pathSegment]?.();
}

export function resolveVirtualCollectionSelection(
	path: ComponentPath | null,
	componentByName: Map<string, ComponentMetadataModel>,
	getComponentByPath: (componentPath: ComponentPath) => IComponentModel | null,
): ResolvedVirtualCollectionSelection | null {
	const collectionItemSelection = getPotentialCollectionItemSelection(path);
	if (!collectionItemSelection) {
		return null;
	}

	const component = getComponentByPath(collectionItemSelection.componentPath);
	if (!component) {
		return null;
	}

	const componentMetadata = componentByName.get(component.Type);
	const metadataParameter = componentMetadata?.parameters?.find((p) => matchesParameterPathSegment(p, collectionItemSelection.parameterPathSegment));
	const normalizedMetadataParameter = normalizeVirtualCollectionParameterFromMetadata(metadataParameter);
	const parameter =
		normalizedMetadataParameter ?? (componentMetadata ? undefined : getLegacyFallbackParameter(component.Type, collectionItemSelection.parameterPathSegment));
	const virtualMetadata = parameter?.typeCustom ? componentByName.get(parameter.typeCustom) : undefined;
	if (!parameter?.isCollection || !virtualMetadata || virtualMetadata.isVirtual !== true) {
		return null;
	}

	return {
		component,
		componentPath: collectionItemSelection.componentPath,
		parameter,
		itemIndex:
			(component.Type === 'ListWithSideDetail' && parameter.name === 'Items') ||
			(component.Type === 'List' && parameter.name === 'Items') ||
			(component.Type === 'ListMessageChat' && parameter.name === 'Message')
				? 0
				: collectionItemSelection.itemIndex,
		virtualMetadata,
	};
}

export function toVirtualComponent(item: Record<string, unknown>, itemIndex: number, virtualType: string): IComponentModel {
	const { Type: itemType, [VIRTUAL_COLLECTION_ITEM_MODEL_TYPE_PROPERTY]: legacyItemType, ...rest } = item;

	return {
		...rest,
		[VIRTUAL_COLLECTION_ITEM_MODEL_TYPE_PROPERTY]: itemType ?? legacyItemType,
		Id: `${virtualType}-${itemIndex}`,
		Type: virtualType,
		Layout: item.Layout ?? {},
	} as IComponentModel;
}

export function createDefaultVirtualCollectionItem(metadata: ComponentMetadataModel): Record<string, unknown> {
	const item: Record<string, unknown> = {};
	for (const parameter of metadata.parameters ?? []) {
		if (!parameter.isRequired) {
			continue;
		}
		if (parameter.defaultValue !== undefined && parameter.defaultValue !== null) {
			item[parameter.name] = parameter.defaultValue;
			continue;
		}
		if (parameter.type === ParameterType.Primitive) {
			if (parameter.expectedType?.type === PrimitiveType.String) {
				item[parameter.name] = '';
			} else if (parameter.expectedType?.type === PrimitiveType.Boolean) {
				item[parameter.name] = false;
			} else if (parameter.expectedType?.type === PrimitiveType.Integer) {
				item[parameter.name] = 0;
			}
		}
	}

	return toVirtualCollectionItem(toVirtualComponent(item, 0, metadata.name));
}

export function toVirtualCollectionItem(component: IComponentModel): Record<string, unknown> {
	const componentRecord = component as unknown as Record<string, unknown>;
	const { Id: _id, Type: _virtualType, Visible: _visible, Layout: layout, [VIRTUAL_COLLECTION_ITEM_MODEL_TYPE_PROPERTY]: itemType, ...item } = componentRecord;

	const hasLayout = layout !== undefined && (typeof layout !== 'object' || layout === null || Object.keys(layout as Record<string, unknown>).length > 0);
	const itemWithLayout = hasLayout ? { ...item, Layout: layout } : item;

	if (itemType !== undefined) {
		return {
			Type: itemType,
			...itemWithLayout,
		};
	}

	return itemWithLayout;
}

export function getCollectionItems(component: IComponentModel, parameter: ParameterMetadata): Record<string, unknown>[] {
	const value = (component as unknown as Record<string, unknown>)[parameter.name];
	if (!Array.isArray(value)) {
		return [];
	}

	return value as Record<string, unknown>[];
}

export function updateComponentCollectionItem(
	component: IComponentModel,
	parameter: ParameterMetadata,
	itemIndex: number,
	updatedItem: Record<string, unknown>,
): IComponentModel {
	const updatedItems = [...getCollectionItems(component, parameter)];
	updatedItems[itemIndex] = updatedItem;

	return {
		...component,
		[parameter.name]: updatedItems,
	};
}

export function removeComponentCollectionItem(component: IComponentModel, parameter: ParameterMetadata, itemIndex: number): IComponentModel {
	const updatedItems = getCollectionItems(component, parameter).filter((_, index) => index !== itemIndex);

	return {
		...component,
		[parameter.name]: updatedItems,
	};
}

export function getPathAfterVirtualCollectionItemDelete(selection: VirtualCollectionSelection, remainingItemCount: number): ComponentPath {
	if (remainingItemCount <= 0) {
		return selection.componentPath;
	}

	return [...selection.componentPath, toPathSegment(selection.parameter.name), String(Math.min(selection.itemIndex, remainingItemCount - 1))];
}
